import React, { useEffect, useMemo, useState } from "react";

import { ShieldCheck, Wallet } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import AddToWalletButton from "./components/AddToWalletButton";
import BenefitsList from "./components/BenefitsList";
import ClaimedState from "./components/ClaimedState";
import HelpEntryCard from "./components/HelpEntryCard";
import IssuerSummary from "./components/IssuerSummary";
import QrCodePanel from "./components/QrCodePanel";
import WalletHelpSheet from "./components/WalletHelpSheet";
import WalletOfferSkeleton from "./components/WalletOfferSkeleton";
import WalletSteps from "./components/WalletSteps";
import WalletStoreFallback from "./components/WalletStoreFallback";
import useDeepLinkFallback from "./hooks/useDeepLinkFallback";
import PageErrorState from "../../common/PageErrorState/PageErrorState";
import EntityConstants from "../../configs/EntityConstants";
import CredIssuerLogo from "../../images/Favicon.svg";
import { GET } from "../../service/CRUDService";
import { pageRootTestIds } from "../../testing/pageRootTestIds";
import { API_ERROR_KIND } from "../../utils/classifyApiError";
import { isHandheldDevice } from "../../utils/device";

const PAGE_BACKGROUND = {
  background:
    "radial-gradient(ellipse 80% 55% at 50% -10%, color-mix(in srgb, var(--cf-primary-color) 14%, transparent), transparent 70%), #f8fafc",
};

const CREDENTIAL_NOT_FOUND = "Credential not found";
const CREDENTIAL_ALREADY_CLAIMED = "Credential already claimed";
const OFFER_URL_NOT_FOUND = "Offer URL not found";
const UNAVAILABLE_ERRORS = new Set([
  OFFER_URL_NOT_FOUND,
  "Credential has no wallet offer",
]);

const getWalletOfferErrorView = (error) => {
  const isOffline =
    error?.isNetworkError ||
    (typeof navigator !== "undefined" && navigator.onLine === false);

  if (isOffline) {
    return { kind: API_ERROR_KIND.OFFLINE, copyKey: "offline", canRetry: true };
  }
  if (error?.message === CREDENTIAL_ALREADY_CLAIMED || error?.status === 409) {
    return { isClaimed: true, claimedAt: error?.claimedAt || null };
  }
  if (UNAVAILABLE_ERRORS.has(error?.message)) {
    return {
      kind: API_ERROR_KIND.NOT_FOUND,
      copyKey: "unavailable",
      canRetry: false,
    };
  }
  if (error?.message === CREDENTIAL_NOT_FOUND || error?.status === 404) {
    return {
      kind: API_ERROR_KIND.NOT_FOUND,
      copyKey: "not_found",
      canRetry: false,
    };
  }
  return { kind: API_ERROR_KIND.SERVER, copyKey: "server", canRetry: true };
};

const PageHeader = ({ subheading }) => {
  const { t } = useTranslation(["walletOffer"]);
  return (
    <header className="mb-6 flex flex-col items-center text-center sm:mb-8">
      <span className="mb-[1rem] flex h-12 w-12 items-center justify-center rounded-2xl bg-cfPrimary text-cfSecondary shadow-[0_10px_24px_-10px_var(--cf-primary-color)]">
        <Wallet className="h-6 w-6" aria-hidden="true" />
      </span>
      <h1 className="text-2xl font-bold tracking-tight text-cfHeadingText sm:text-3xl">
        {t("walletOffer:heading")}
      </h1>
      {subheading ? (
        <p className="mt-2 max-w-xl text-sm leading-relaxed text-gray-500 sm:text-base">
          {subheading}
        </p>
      ) : null}
    </header>
  );
};

const BLOB_FILL = "color-mix(in srgb, var(--cf-primary-color) 7%, transparent)";

const BackgroundDecor = () => (
  <div
    aria-hidden="true"
    className="pointer-events-none absolute inset-0 overflow-hidden"
  >
    <span
      className="absolute -left-40 top-1/2 h-[26rem] w-[26rem] rounded-full blur-2xl"
      style={{ backgroundColor: BLOB_FILL }}
    />
    <span
      className="absolute -right-32 top-24 h-[22rem] w-[22rem] rounded-full blur-2xl"
      style={{ backgroundColor: BLOB_FILL }}
    />
    <span
      className="absolute -bottom-24 right-1/4 h-64 w-64 rounded-full blur-2xl"
      style={{ backgroundColor: BLOB_FILL }}
    />
  </div>
);

const PoweredByFooter = () => {
  const { t } = useTranslation(["walletOffer"]);
  return (
    <footer className="relative flex items-center justify-center gap-2 px-[1rem] pb-6 pt-2 text-xs text-gray-400">
      <span>{t("walletOffer:powered_by")}</span>
      <img src={CredIssuerLogo} alt="CredIssuer" className="h-4 w-auto" />
    </footer>
  );
};

const DesktopOffer = ({ offer }) => (
  <div className="flex flex-col gap-[1rem]">
    <section className="animate-cert-card-in overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-[0_24px_60px_-28px_rgba(16,24,40,0.25)]">
      <div className="grid md:grid-cols-[minmax(0,1fr)_auto]">
        <div className="flex flex-col justify-between gap-4 p-6">
          <IssuerSummary
            issuer={offer.issuer}
            credentialName={offer.credential_name}
            credentialFormat={offer.credential_format}
            credentialId={offer.credential_id}
          />
          <BenefitsList />
        </div>
        <div className="flex items-center justify-center border-t border-gray-100 bg-gray-50 p-6 md:border-l md:border-t-0">
          <QrCodePanel qrCode={offer.qr_code} offerUrl={offer.offer_url} />
        </div>
      </div>
      <div className="border-t border-gray-100 p-6">
        <WalletSteps
          credentialName={offer.credential_name}
          issuerLogoUrl={offer.issuer?.logo_url}
        />
      </div>
    </section>

    <WalletStoreFallback
      iosUrl={offer.wallet_ios_link}
      androidUrl={offer.wallet_playstore_link}
    />
  </div>
);

const HandheldOffer = ({ offer, walletHref }) => {
  const { t } = useTranslation(["walletOffer"]);
  const { startLaunch, isLaunching } = useDeepLinkFallback();
  const [isHelpOpen, setIsHelpOpen] = useState(false);

  return (
    <div className="mx-auto flex w-full max-w-md animate-cert-card-in flex-col gap-[1rem]">
      <section className="flex flex-col gap-6 rounded-3xl border border-gray-200 bg-white p-[1.25rem] shadow-[0_24px_60px_-28px_rgba(16,24,40,0.25)] sm:p-6">
        <IssuerSummary
          issuer={offer.issuer}
          credentialName={offer.credential_name}
          credentialFormat={offer.credential_format}
          credentialId={offer.credential_id}
        />

        <div className="flex flex-col gap-3">
          <AddToWalletButton
            href={walletHref}
            onLaunch={startLaunch}
            isLaunching={isLaunching}
          />
          <p className="flex items-center justify-center gap-1.5 text-center text-xs text-gray-500">
            <ShieldCheck
              className="h-3.5 w-3.5 shrink-0 text-cfPrimary"
              aria-hidden="true"
            />
            {t("walletOffer:handheld_secure_note")}
          </p>
        </div>
      </section>

      <WalletStoreFallback
        iosUrl={offer.wallet_ios_link}
        androidUrl={offer.wallet_playstore_link}
      />

      <HelpEntryCard onClick={() => setIsHelpOpen(true)} />
      <WalletHelpSheet
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        credentialName={offer.credential_name}
        issuerName={offer.issuer?.name}
        issuerLogoUrl={offer.issuer?.logo_url}
      />
    </div>
  );
};

const WalletOfferPage = () => {
  const { credentialId } = useParams();
  const { t } = useTranslation(["walletOffer"]);
  const isHandheld = useMemo(isHandheldDevice, []);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRetrying, setIsRetrying] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    setData(null);
    setError(null);
    setIsLoading(true);
  }, [credentialId]);

  useEffect(() => {
    const controller = new AbortController();

    const fetchWalletOffer = async () => {
      try {
        const response = await GET(
          `${EntityConstants.GET_WALLET_OFFER}/${encodeURIComponent(credentialId)}`,
          { signal: controller.signal },
        );
        if (!response) {
          setError({ message: "" });
          return;
        }

        let responseData = null;
        try {
          responseData = await response.json();
        } catch {
          responseData = null;
        }

        if (response.ok && responseData) {
          setData(responseData);
          setError(null);
        } else {
          setError({
            status: response.status,
            message:
              responseData?.error ||
              responseData?.detail ||
              responseData?.message ||
              "",
            claimedAt: responseData?.claimed_at || null,
          });
        }
      } catch (fetchError) {
        if (fetchError?.name === "AbortError") return;
        setError({ isNetworkError: true, message: fetchError?.message || "" });
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
          setIsRetrying(false);
        }
      }
    };

    fetchWalletOffer();
    return () => controller.abort();
  }, [credentialId, attempt]);

  const retry = () => {
    setIsRetrying(true);
    setAttempt((value) => value + 1);
  };

  const walletHref = data?.wallet_link || data?.offer_url || "";
  const hasUsableOffer = isHandheld
    ? Boolean(walletHref)
    : Boolean(data?.qr_code || data?.offer_url);
  const isSuccess = !isLoading && !error && hasUsableOffer;
  const errorView = isLoading
    ? null
    : error
      ? getWalletOfferErrorView(error)
      : !hasUsableOffer
        ? getWalletOfferErrorView({ message: OFFER_URL_NOT_FOUND })
        : null;

  const isClaimed = Boolean(errorView?.isClaimed);

  useEffect(() => {
    const previousTitle = document.title;
    if (isClaimed) {
      document.title = t("walletOffer:document_title_claimed");
    } else {
      document.title = data?.credential_name
        ? t("walletOffer:document_title", {
            credentialName: data.credential_name,
          })
        : t("walletOffer:document_title_default");
    }
    return () => {
      document.title = previousTitle;
    };
  }, [data?.credential_name, isClaimed, t]);

  const subheading = isHandheld
    ? t("walletOffer:subheading_handheld")
    : t("walletOffer:subheading_desktop");

  return (
    <div
      id={pageRootTestIds.walletOffer}
      className="relative flex min-h-screen w-full flex-col overflow-x-hidden font-CfPrimaryFont text-cfHeadingText"
      style={PAGE_BACKGROUND}
    >
      {isHandheld ? null : <BackgroundDecor />}
      <div className="relative flex flex-1 items-center justify-center px-[1rem] py-8">
        <div className="mx-auto w-full max-w-screen-2xl">
          {errorView?.isClaimed ? (
            <div className="mx-auto max-w-lg">
              <ClaimedState claimedAt={errorView.claimedAt} />
            </div>
          ) : errorView ? (
            <div className="mx-auto max-w-lg animate-fadeIn">
              <PageErrorState
                kind={errorView.kind}
                title={t(`walletOffer:error_${errorView.copyKey}_title`)}
                description={t(
                  `walletOffer:error_${errorView.copyKey}_description`,
                )}
                primaryLabel={
                  errorView.canRetry ? t("walletOffer:retry") : null
                }
                onPrimary={errorView.canRetry ? retry : null}
                isRetrying={isRetrying}
              />
            </div>
          ) : (
            <>
              <PageHeader subheading={subheading} />
              {isSuccess ? (
                isHandheld ? (
                  <HandheldOffer offer={data} walletHref={walletHref} />
                ) : (
                  <DesktopOffer offer={data} />
                )
              ) : (
                <WalletOfferSkeleton isHandheld={isHandheld} />
              )}
            </>
          )}
        </div>
      </div>
      <PoweredByFooter />
    </div>
  );
};

export default WalletOfferPage;
